import { useEffect, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Stars } from "@/components/Stars";

const input = "w-full rounded-xl border border-[#E2D7C3] bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#8C2220]/30";
const EMPTY = { order_number: "", email: "", name: "", rating: 0, text: "" };

const ReviewForm = ({ pid, onDone, prefill }) => {
  const [f, setF] = useState({ ...EMPTY, order_number: prefill });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error("Please choose a star rating");
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${pid}/reviews`, f);
      toast.success(data.message);
      setF(EMPTY);
      onDone();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-[#EDE4D3] p-5 space-y-3" data-testid="review-form">
      <p className="font-semibold">Write a review</p>
      <p className="text-xs text-slate-500">Reviews are for customers who bought this item. Enter your order number and email so we can check.</p>
      <div className="flex gap-1" data-testid="review-rating-input">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} aria-label={`${n} stars`} data-testid={`review-star-${n}`}>
            <Star className={`w-7 h-7 transition-colors ${n <= f.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-slate-300 hover:text-[#D4AF37]"}`} />
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required value={f.order_number} onChange={set("order_number")} placeholder="Order number e.g. AP-123456" className={input} data-testid="review-order-number" />
        <input required type="email" value={f.email} onChange={set("email")} placeholder="Email used for the order" className={input} data-testid="review-email" />
      </div>
      <input required maxLength={60} value={f.name} onChange={set("name")} placeholder="Your first name" className={input} data-testid="review-name" />
      <textarea required minLength={3} maxLength={1500} rows={3} value={f.text} onChange={set("text")} placeholder="What did you think?" className={input} data-testid="review-text" />
      <button disabled={busy} className="rounded-full bg-[#8C2220] text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="review-submit">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit review
      </button>
    </form>
  );
};

export default function Reviews({ p }) {
  const [reviews, setReviews] = useState([]);
  const prefill = new URLSearchParams(window.location.search).get("review") || "";
  const load = () => api.get(`/products/${p.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section id="reviews" className="mt-16 grid lg:grid-cols-[1fr_420px] gap-10 scroll-mt-28" data-testid="reviews-section">
      <div>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold">Customer reviews</h2>
        {p.rating_count > 0 ? <Stars value={p.rating_avg} count={p.rating_count} size="w-5 h-5" className="mt-2" /> : <p className="mt-2 text-slate-500" data-testid="no-reviews">No reviews yet. Bought this? Be the first to review it.</p>}
        <div className="mt-6 divide-y divide-[#EDE4D3]">
          {reviews.map((r) => (
            <article key={r.id} className="py-5" data-testid={`review-${r.id}`}>
              <Stars value={r.rating} />
              <p className="mt-2 text-slate-700 leading-relaxed">{r.text}</p>
              <p className="mt-2 text-xs text-slate-500">{r.name} · Verified buyer · {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
            </article>
          ))}
        </div>
      </div>
      <ReviewForm pid={p.id} onDone={load} prefill={prefill} />
    </section>
  );
}
